import React from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { CountrySelect } from '../common/CountrySelect';
import type { SupplierCompanyProfile } from '../../types/types';
import SupplierProfileBuilderCountryBadge from './SupplierProfileBuilderCountryBadge';

interface SupplierProfileBuilderStepSupplyChainTransparencyProps {
  profile: SupplierCompanyProfile;
  setProfile: React.Dispatch<React.SetStateAction<SupplierCompanyProfile>>;
  tempOriginCountry: string;
  setTempOriginCountry: React.Dispatch<React.SetStateAction<string>>;
  setChangesSaved: React.Dispatch<React.SetStateAction<boolean>>;
  addOriginCountry: () => void;
  countries: { id: number; name: string; code: string }[];
}

const SupplierProfileBuilderStepSupplyChainTransparency: React.FC<
  SupplierProfileBuilderStepSupplyChainTransparencyProps
> = ({
  profile,
  setProfile,
  tempOriginCountry,
  setTempOriginCountry,
  setChangesSaved,
  addOriginCountry,
  countries,
}) => {
  return (
    <div>
      <Row>
        <Col>
          <p className="text-muted">
            This section focuses on your supply chain transparency, including
            how much you know about your suppliers and where your raw materials
            come from. Providing this information can help build trust with
            buyers who are increasingly concerned about ethical sourcing and
            supply chain risks.
          </p>
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Tier 1 Visibility (Direct Suppliers)</Form.Label>
            <Form.Select
              value={profile.tier_1_visibility_level || 'unknown'}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  tier_1_visibility_level: e.target.value,
                });
                setChangesSaved(false);
              }}
            >
              <option value="unknown">Unknown</option>
              <option value="identified">
                Identified (We know who they are)
              </option>
              <option value="risk_assessed">
                Risk Assessed (Screened for ESG)
              </option>
              <option value="verified">Verified (Audited/Certified)</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Tier 2 Visibility (Sub-suppliers)</Form.Label>
            <Form.Select
              value={profile.tier_2_visibility_level || 'none'}
              onChange={(e) => {
                setProfile({
                  ...profile,
                  tier_2_visibility_level: e.target.value,
                });
                setChangesSaved(false);
              }}
            >
              <option value="none">No Visibility</option>
              <option value="critical_only">Critical Path Only</option>
              <option value="partial">Partial Mapping (~50%)</option>
              <option value="full">Full Transparency</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Raw Material Origins (Countries)</Form.Label>
            <div className="d-flex gap-2 mb-2">
              <div className="flex-grow-1">
                <CountrySelect
                  full
                  value={tempOriginCountry}
                  onChange={(val) => setTempOriginCountry(val as string)}
                />
              </div>
              <Button variant="outline-primary" onClick={addOriginCountry}>
                Add
              </Button>
            </div>
            <div className="d-flex flex-wrap gap-2">
              {profile.raw_material_origins?.map((code: string) => {
                const country = countries?.find((c) => c.code === code);
                const countryName = country ? country.name : code;
                return (
                  <SupplierProfileBuilderCountryBadge
                    key={code}
                    countryId={country ? country.id.toString() : '0'}
                    countryName={countryName}
                    removeCountry={() => {
                      setProfile({
                        ...profile,
                        raw_material_origins:
                          profile.raw_material_origins?.filter(
                            (c: string) => c !== code,
                          ) || [],
                      });
                      setChangesSaved(false);
                    }}
                  />
                );
              })}
            </div>
          </Form.Group>
        </Col>
      </Row>
    </div>
  );
};

export default SupplierProfileBuilderStepSupplyChainTransparency;
